import { motion } from "framer-motion";
import {
    AlertTriangle,
    Archive,
    ArrowLeft,
    BellOff,
    BellRing,
    BookmarkPlus,
    Check,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    Clock,
    Eye,
    FileDown,
    Lightbulb,
    ListChecks,
    Loader2,
    Lock,
    Mail,
    MapPin,
    MessageCircle,
    MessageSquare,
    Phone,
    PhoneOff,
    RefreshCw,
    Send,
    Sparkles,
    Target,
    ThumbsDown,
    Trash2,
    Trophy,
    Undo2,
    UserX,
    Pencil,
    ScanLine,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import ScoreBreakdown from "../components/candidates/ScoreBreakdown";
import ScoreRing from "../components/candidates/ScoreRing";
import ScheduleImmersionModal from "../components/candidates/ScheduleImmersionModal";
import ImmersionPlanCard from "../components/candidates/ImmersionPlanCard";
import OnboardingCard from "../components/candidates/OnboardingCard";
import PaieTerrainCard from "../components/candidates/PaieTerrainCard";
import BadgeCard from "../components/BadgeCard";
import IsTestToggle from "../components/candidates/IsTestToggle";
import CodeRdBadge from "../components/candidates/CodeRdBadge";
import EditIdentityModal from "../components/candidates/EditIdentityModal";
import IdOcrModal from "../components/candidates/IdOcrModal";
import ContractCard from "../components/candidates/ContractCard";
import EndTrialButton from "../components/candidates/EndTrialButton";
import PostImmersionDecisionModal from "../components/candidates/PostImmersionDecisionModal";
import LastDecisionBanner from "../components/candidates/LastDecisionBanner";
import RejectionAuditTrail from "../components/candidates/RejectionAuditTrail";
import ActionTimeline from "../components/candidates/ActionTimeline";
import SmartBadge from "../components/candidates/SmartBadge";
import TalentPoolModal from "../components/candidates/TalentPoolModal";
import EvaluationCard from "../components/candidates/EvaluationCard";
import CallActionModal from "../components/scorecard/CallActionModal";
import ConfirmModal from "../components/common/ConfirmModal";
import MessageHistory from "../components/messaging/MessageHistory";
import SendMessageModal from "../components/messaging/SendMessageModal";
import CallButton from "../components/call/CallButton";
import CallHistory from "../components/call/CallHistory";
import FastTrackButton from "../components/candidates/FastTrackButton";
import RemoveFromOnboardingButton from "../components/candidates/RemoveFromOnboardingButton";
import ForceIncludeInPipelineButton from "../components/candidates/ForceIncludeInPipelineButton";
import RejectReasonModal from "../components/candidates/RejectReasonModal";
import RegionReassignCard from "../components/candidates/RegionReassignCard";
import { api } from "../lib/api";
import { getQueuePosition, removeFromQueue } from "../lib/callQueue";
import {
    PROFILE_META,
    RISK_META,
    STATUS_META,
    SUB_SCORE_LABELS,
} from "../lib/constants";
import { useAuth } from "../lib/auth";
import { formatDateTime } from "../lib/format";
import { goBack } from "../lib/navigation";
import { hasRole } from "../lib/roles";

export default function CandidateProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rescoring, setRescoring] = useState(false);
    const [accessError, setAccessError] = useState(null);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [schedulingImmersion, setSchedulingImmersion] = useState(false);
    // Confirmation modal for cancelling an existing immersion from the profile.
    const [cancellingImmersion, setCancellingImmersion] = useState(false);
    // Post-immersion decision modal (validate / reject after immersion).
    const [decidingImmersion, setDecidingImmersion] = useState(false);
    // Messaging
    const [messagingState, setMessagingState] = useState(null); // {category} | null
    const [historyTick, setHistoryTick] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [editingIdentity, setEditingIdentity] = useState(false);
    // Boutons « Modifier les informations » (dossier d'embauche + contrat).
    useEffect(() => {
        const open = (e) => { if (e?.detail?.candidate_id === id) setEditingIdentity(true); };
        window.addEventListener("prerec:open-identity-edit", open);
        return () => window.removeEventListener("prerec:open-identity-edit", open);
    }, [id]);
    const [idOcrOpen, setIdOcrOpen] = useState(false);
    const [talentPoolModal, setTalentPoolModal] = useState(null); // { defaultReason } | null
    const [qualifying, setQualifying] = useState(false);
    // "Qualifier" multi-action modal (Programmer immersion / Pas de réponse / Plus dispo / Refus / Vivier)
    const [showCallActionModal, setShowCallActionModal] = useState(false);
    // iter97 · Force reject reason selection — modal opens before any reject action
    const [rejectModal, setRejectModal] = useState(null); // { defaultReason } | null
    // Criteria registry used to resolve criterion_id → human-readable label
    // when rendering the evaluation breakdown. Pre-rec criteria can be
    // overridden per candidate by the entretien_script; immersion criteria
    // are the static IMMERSION_CRITERIA. Loaded once.
    const [criteriaMap, setCriteriaMap] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/candidates/criteria");
                if (cancelled) return;
                const map = {};
                [...(data.prerec || []), ...(data.immersion || [])].forEach((c) => {
                    map[c.id] = { label: c.label, block: c.block, weight: c.weight };
                });
                setCriteriaMap(map);
            } catch { /* fall back to displaying raw ids */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        try {
            const { data: payload } = await api.get(`/candidates/${id}`);
            setData(payload);
            setAccessError(null);
        } catch (e) {
            if (e.response?.status === 403) {
                setAccessError({
                    code: 403,
                    detail: e.response?.data?.detail || "Hors de votre périmètre.",
                });
            } else if (e.response?.status === 404) {
                setAccessError({ code: 404, detail: "Candidat introuvable" });
            } else {
                setAccessError({ code: 0, detail: "Erreur de chargement" });
            }
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    // iter101 · Scroll vers l'ancre (#onboarding / #contract) après chargement
    // du candidat — permet aux CTAs du drawer /embauche de pointer
    // directement vers la bonne section de la fiche.
    useEffect(() => {
        if (loading || !location.hash) return;
        const targetId = location.hash.slice(1);
        // Attendre un tick pour que React ait rendu les sections
        const t = setTimeout(() => {
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.classList.add("ring-2", "ring-shariti-orange", "ring-offset-2", "rounded-3xl");
                setTimeout(() => {
                    el.classList.remove("ring-2", "ring-shariti-orange", "ring-offset-2", "rounded-3xl");
                }, 2500);
            }
        }, 100);
        return () => clearTimeout(t);
    }, [loading, location.hash]);

    // Queue navigation — derived from localStorage saved by Candidates.jsx
    const queueInfo = useMemo(() => getQueuePosition(id), [id]);

    // Keyboard navigation ← / → between candidates in the queue
    useEffect(() => {
        function handleKey(e) {
            const tag = e.target?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select") return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === "ArrowLeft" && queueInfo?.prevId) {
                navigate(`/candidats/${queueInfo.prevId}`);
            } else if (e.key === "ArrowRight" && queueInfo?.nextId) {
                navigate(`/candidats/${queueInfo.nextId}`);
            }
        }
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [navigate, queueInfo]);

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    if (accessError) {
        const isForbidden = accessError.code === 403;
        return (
            <div className="card-soft p-10 text-center max-w-lg mx-auto mt-12" data-testid="profile-access-error">
                <div className={`h-14 w-14 rounded-2xl mx-auto inline-flex items-center justify-center ${isForbidden ? "bg-red-50 text-shariti-red" : "bg-shariti-cream text-slate-500"}`}>
                    {isForbidden ? <Lock className="h-7 w-7" /> : <AlertTriangle className="h-7 w-7" />}
                </div>
                <h2 className="mt-4 text-2xl font-black tracking-tight text-shariti-black">
                    {isForbidden ? "Hors de votre périmètre" : "Candidat indisponible"}
                </h2>
                <p className="mt-2 text-sm font-medium text-slate-500">
                    {accessError.detail}
                </p>
                <button
                    onClick={() => goBack(navigate)}
                    className="mt-6 btn-shariti"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                </button>
            </div>
        );
    }

    if (!data) return null;

    const c = data.candidate;
    const risk = RISK_META[c.risk_level] || RISK_META.low;
    const status = STATUS_META[c.status] || STATUS_META.new;
    const subScores = c.sub_scores || {};
    const isAdmin = hasRole(user, "admin");
    const isReferent = hasRole(user, "referent");
    const isPreRecruteur = hasRole(user, "pre_recruteur");
    // Pre_rec is visible to admin, pre_recruteur, and composite (pre_rec + referent).
    // Immersion is visible to admin, referent, and composite (pre_rec + referent) — NOT to pure pre-recruiters.
    const canSeePrerec = isAdmin || isPreRecruteur;
    const canSeeImmersion = isAdmin || isReferent;
    // Terminal status: the candidate is in an end state — no more "Refuser",
    // no more "Scorecard Immersion", no more "IMMERSION PROGRAMMÉE" banner.
    // These are statuses where the decision has been made (positive or negative).
    const isTerminalStatus = ["rejected", "hired", "onboarding", "validated", "immersion_done"].includes(c.status) || c.in_talent_pool;
    // After the field immersion has been acted upon (scorecard submitted, or
    // candidate validated/rejected post-immersion), the planning UI must
    // disappear — the immersion is in the past, not "à venir".
    const immersionStillUpcoming = c.status === "immersion_scheduled";

    async function handleRescore() {
        setRescoring(true);
        try {
            await api.post(`/candidates/${id}/rescore`);
            toast.success("Re-scoring IA effectué");
            await load();
        } catch {
            toast.error("Impossible de re-scorer");
        } finally {
            setRescoring(false);
        }
    }

    async function handleValidate() {
        try {
            await api.post(`/candidates/${id}/validate`);
            toast.success("Candidat validé");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleOnboarding() {
        try {
            await api.post(`/candidates/${id}/onboarding`);
            toast.success("Onboarding envoyé");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleReject(reason) {
        try {
            await api.post(`/candidates/${id}/reject`, reason ? { reason } : {});
            // iter96 · Retirer le candidat refusé de la file d'appel locale
            // (localStorage) pour que prev/next ne pointe plus vers lui et
            // qu'un retour sur la liste `?status=to_recall` le fasse
            // disparaître immédiatement.
            removeFromQueue(id);
            toast.success("Candidat refusé");
            await load();
        } catch {
            toast.error("Erreur");
        }
    }

    async function setStatusSilently(newStatus) {
        try {
            await api.patch(`/candidates/${id}`, { status: newStatus });
            toast.success("Statut mis à jour");
            await load();
        } catch {
            toast.error("Erreur");
        }
    }

    function askStatusChange(newStatus, opts) {
        setPendingStatusChange({ status: newStatus, ...opts });
    }

    async function handleRevertStatus() {
        try {
            const { data: updated } = await api.post(`/candidates/${id}/revert-status`);
            toast.success(`Statut restauré : ${updated.status}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de revenir en arrière");
        }
    }

    async function handleUndoNoAnswer() {
        try {
            await api.post(`/candidates/${id}/undo-no-answer`);
            toast.success("Pas de réponse annulé");
            await load();
        } catch {
            toast.error("Impossible d'annuler");
        }
    }

    /**
     * Quick "Qualifier" action — same flow as the one inside the Scorecard:
     * POST /candidates/{id}/no-answer, optionally open the SMS recap modal to
     * push a "Je vous recontacte" message to the candidate. This lets the
     * pre-recruiter mark a candidate as "appelé · sans réponse" directly from
     * the profile (no need to enter the scorecard).
     */
    async function handleQualifyNoAnswer() {
        if (qualifying) return;
        setQualifying(true);
        try {
            await api.post(`/candidates/${id}/no-answer`);
            toast.success("Candidat marqué « appelé · sans réponse »");
            await load();
            // Offer to send the no-answer SMS right after, like in the scorecard flow.
            setMessagingState({ category: "no_answer" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de qualifier");
        } finally {
            setQualifying(false);
        }
    }

    async function handleHardDelete() {
        setConfirmDelete(false);
        try {
            await api.delete(`/candidates/${id}`);
            toast.success(`${c.first_name} ${c.last_name} supprimé définitivement`);
            navigate(queueInfo?.return_url || "/candidats");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Suppression impossible");
        }
    }

    async function handleResetAttempts() {
        if (!window.confirm(`Annuler les ${c.call_attempts} tentatives ? Le candidat sera considéré comme jamais appelé.`)) return;
        try {
            await api.post(`/candidates/${id}/reset-call-attempts`);
            toast.success("Toutes les tentatives ont été annulées");
            await load();
        } catch {
            toast.error("Impossible de réinitialiser");
        }
    }

    const hasStatusHistory = (c.status_history || []).length > 0;
    const lastStatusChange = hasStatusHistory ? c.status_history[c.status_history.length - 1] : null;

    // Compute the last *meaningful* decision so we can surface a clear banner
    // (who/when/why) under the funnel. Mirrors the server-side
    // `_extract_last_decision` in routes/immersions.py so the calendar and
    // profile views display the exact same wording.
    const REASON_LABELS_MAP = {
        profil_ideal: "Profil idéal pour la mission",
        bonne_posture_terrain: "Bonne posture terrain",
        validation_directe: "Validation directe sans réserve",
        candidat_indisponible: "Candidat plus disponible",
        criteres_non_remplis: "Critères non remplis",
        immersion_inadequat: "Inadapté au terrain",
        immersion_comportement: "Comportement inadéquat",
        immersion_no_show: "Candidat non présenté",
        immersion_candidat_retire: "Candidat retiré",
        autre: "Autre motif",
    };
    let lastDecision = null;
    if (c.immersion_no_show) {
        lastDecision = {
            kind: "no_show",
            by_name: c.immersion_no_show_by_name,
            at: c.immersion_no_show_at,
        };
    } else if (hasStatusHistory) {
        const TARGETS = new Set(["rejected", "validated", "immersion_done"]);
        // Detect a fresh un-rejection so we can hide the red banner
        // once the admin has clicked "Annuler le refus". The banner would
        // otherwise keep showing "Refusé — motif non précisé" indefinitely
        // because the rejected entry is still in history.
        // CEO 2026-09-07 : « la grosse mention "Refusé, motif non précisé"
        // devrait notamment disparaître une fois que je clique sur
        // Annuler le refus. »
        let rejectionActive = c.status === "rejected";
        for (let i = c.status_history.length - 1; i >= 0; i--) {
            const h = c.status_history[i];
            if (TARGETS.has(h.to_status)) {
                if (h.to_status === "rejected" && !rejectionActive) {
                    // Skip this rejected entry — it has since been undone.
                    // The <RejectionAuditTrail> block below takes over the
                    // "who / when / why" surface for undone rejections.
                    continue;
                }
                lastDecision = {
                    kind: h.to_status,
                    by_name: h.by_name,
                    at: h.at,
                    reason: h.reason,
                    reason_label: h.reason ? REASON_LABELS_MAP[h.reason] || null : null,
                    comment: h.comment,
                };
                break;
            }
        }
    }

    return (
        <div className="space-y-4 md:space-y-6" data-testid="candidate-profile">
            {/* Sticky top: Retour + Queue nav + Funnel pipeline — stay together during scroll */}
            <div
                className="sticky top-14 md:top-16 z-20 -mx-4 md:-mx-8 px-3 md:px-8 pt-2 md:pt-3 pb-2 bg-shariti-cream border-b-2 border-shariti-gray/60 space-y-2 md:space-y-3 shadow-md"
                data-testid="profile-sticky-header"
            >
                <div className="flex items-center gap-2 md:gap-3" data-testid="profile-top-nav">
                    <button
                        onClick={() => goBack(navigate)}
                        data-testid="profile-back"
                        className="inline-flex items-center gap-1.5 md:gap-2 bg-white text-slate-600 border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange rounded-full font-extrabold px-3 md:px-4 py-2 text-xs md:text-sm transition shrink-0"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Retour</span>
                    </button>

                    {queueInfo && (
                        <div
                            className="flex items-center gap-1.5 md:gap-3 ml-auto rounded-full bg-shariti-orange-soft/60 border border-shariti-orange/20 pl-2 md:pl-3 pr-1 md:pr-1.5 py-1 min-w-0"
                            data-testid="profile-queue-nav"
                        >
                            <ListChecks className="h-4 w-4 text-shariti-orange shrink-0" />
                            <div className="hidden md:block text-[10px] font-bold uppercase tracking-widest text-shariti-orange truncate max-w-[280px]" title={queueInfo.label}>
                                File · {queueInfo.label}
                            </div>
                            <div className="text-[11px] md:text-xs font-extrabold text-shariti-black whitespace-nowrap">
                                {queueInfo.position}/{queueInfo.total}
                            </div>
                            <div className="flex items-center gap-1 md:gap-1.5">
                                <button
                                    onClick={() => queueInfo.prevId && navigate(`/candidats/${queueInfo.prevId}`)}
                                    disabled={!queueInfo.prevId}
                                    data-testid="profile-queue-prev"
                                    title="Candidat précédent (←)"
                                    aria-label="Candidat précédent"
                                    className="h-8 w-8 md:w-auto md:px-3 inline-flex items-center justify-center md:gap-1 rounded-full bg-white border border-shariti-orange/30 text-shariti-orange hover:bg-shariti-orange hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-extrabold"
                                >
                                    <ChevronLeft className="h-3.5 w-3.5" />
                                    <span className="hidden md:inline">Précédent</span>
                                </button>
                                <button
                                    onClick={() => queueInfo.nextId && navigate(`/candidats/${queueInfo.nextId}`)}
                                    disabled={!queueInfo.nextId}
                                    data-testid="profile-queue-next"
                                    title="Candidat suivant (→)"
                                    aria-label="Candidat suivant"
                                    className="h-8 w-8 md:w-auto md:px-3 inline-flex items-center justify-center md:gap-1 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-extrabold shadow-btn"
                                >
                                    <span className="hidden md:inline">Suivant</span>
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <FunnelStrip status={c.status} candidate={c} onUnrejected={load} />
            </div>

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="card-soft p-6 md:p-8"
            >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                    {c.avatar_url ? (
                        <img
                            src={c.avatar_url}
                            alt=""
                            className="h-20 w-20 md:h-24 md:w-24 rounded-2xl object-cover shrink-0"
                        />
                    ) : (
                        <div className="h-20 w-20 md:h-24 md:w-24 rounded-2xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-3xl flex items-center justify-center shrink-0">
                            {c.first_name?.[0]}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-shariti-black" data-testid="profile-header-name">
                                {c.first_name} {c.last_name}
                                {(c.prenom_usage || c.nom_usage) && (
                                    <span
                                        className="ml-2 text-lg md:text-xl font-bold text-slate-400"
                                        data-testid="profile-header-usage"
                                        title="Nom d'usage terrain (Evergiving / plannings). Les documents légaux (contrats, PayFit) restent sur le nom légal."
                                    >
                                        ({c.prenom_usage || c.first_name} {c.nom_usage || c.last_name})
                                    </span>
                                )}
                            </h1>
                            {/* iter97 · CEO 2026-09-02 : « Le motif de refus
                                doit apparaître aussi en grand à côté du nom
                                de la personne concernée. » Badge XL rouge
                                collé au titre pour être vu au premier coup
                                d'œil (avant même le bouton "Focus"). */}
                            {c.status === "rejected" && (c.rejection_meta || c.reject_reason) && (
                                <span
                                    data-testid="profile-header-reject-badge"
                                    title={
                                        c.rejection_meta?.by_name
                                            ? `Refusé par ${c.rejection_meta.by_name}${c.rejection_meta.comment ? ` · ${c.rejection_meta.comment}` : ""}`
                                            : ""
                                    }
                                    className="inline-flex items-center gap-1.5 text-sm md:text-base font-black uppercase tracking-wide px-3 py-1.5 rounded-full border-2 bg-red-50 text-red-700 border-red-400 shadow-sm"
                                >
                                    ✕ REFUSÉ — {c.rejection_meta?.reason_label
                                        || c.reject_reason?.replace(/_/g, " ")
                                        || "motif non précisé"}
                                </span>
                            )}
                            {/* Discreet pen icon — opens the identity
                                correction modal. Any authenticated user
                                with write access to the profile can
                                trigger it (RBAC enforced server-side).
                                The modal preserves `code_rd` unconditionally. */}
                            <button
                                type="button"
                                onClick={() => setEditingIdentity(true)}
                                data-testid="profile-edit-identity-btn"
                                title="Corriger une erreur de frappe (prénom, nom, email…) — le matricule est conservé"
                                className="inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft transition"
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {/* OCR pièce d'identité — Claude Sonnet 4.5 vision
                                lit la CNI/passeport téléversé par le candidat et
                                propose les valeurs à appliquer. Admin/Référent
                                seulement (RBAC serveur). Une fois appliqué, la
                                fiche se synchronise sur onboarding_forms (iter60)
                                et alimente le contrat SYNTEC (iter62).
                                Iter64: si l'auto-OCR au moment de l'upload a
                                déjà tourné, la fiche a un flag `id_ocr_available`
                                → on affiche une petite pastille verte sur le
                                bouton pour indiquer que l'extraction est prête. */}
                            <button
                                type="button"
                                onClick={() => setIdOcrOpen(true)}
                                data-testid="profile-id-ocr-btn"
                                title={
                                    c.id_ocr_available
                                        ? "Extraction pré-calculée disponible — cliquer pour comparer"
                                        : "Lire automatiquement la pièce d'identité (CNI, titre de séjour, passeport)"
                                }
                                className="relative inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft transition"
                            >
                                <ScanLine className="h-3.5 w-3.5" />
                                {c.id_ocr_available && (
                                    <span
                                        data-testid="profile-id-ocr-available-dot"
                                        className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white"
                                    />
                                )}
                            </button>
                            {/* Matricule Shariti — the universal join key.
                                Visible RIGHT next to the name so admins /
                                ops spot it in a glance (per CEO brief
                                2026-07-29 point 1). Click copies to
                                clipboard for pasting into Evergiving. */}
                            <CodeRdBadge
                                code={c.code_rd}
                                candidateId={c.id}
                                size="lg"
                            />
                            {/* Admin-only toggle to flip is_test — safety net
                                if the auto-detection ever flags a real
                                collaborator by mistake. For non-admins the
                                component renders the classic read-only
                                "🧪 CANDIDAT TEST" pill when flagged, so the
                                UX is unchanged for pre-recruteurs / referents.
                                All flips are journalled server-side into
                                `is_test_history[]`. */}
                            <IsTestToggle candidate={c} user={user} onChange={load} />
                            {c.in_talent_pool && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full" data-testid="profile-pool-badge">
                                    <Archive className="h-3 w-3" />
                                    Au vivier
                                </span>
                            )}
                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${status.color}`}>
                                {status.label}
                            </span>
                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${risk.bg} ${risk.color}`}>
                                Risque {risk.label}
                            </span>
                            {c.cv_url && (
                                <a
                                    href={c.cv_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="profile-download-cv-top"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-shariti-black text-white hover:bg-slate-800 px-3 py-1 text-[11px] font-extrabold transition shadow-sm"
                                    title="Télécharger le CV original (PDF)"
                                >
                                    <FileDown className="h-3.5 w-3.5" />
                                    CV PDF
                                </a>
                            )}
                            <Link
                                to={`/candidats/${c.id}/recap`}
                                data-testid="profile-recap-link"
                                className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white hover:bg-[#d66528] px-3 py-1 text-[11px] font-extrabold transition shadow-btn"
                                title="Vue récap 360° — synthèse en un coup d'œil"
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                Récap 360°
                            </Link>
                            {canSeePrerec && (
                                <Link
                                    to={`/candidats/${c.id}/focus`}
                                    data-testid="profile-focus-link"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-shariti-black text-white hover:bg-slate-800 px-3 py-1 text-[11px] font-extrabold transition shadow-btn"
                                    title="Mode Focus — vue plein écran pour enchaîner les appels (← / →)"
                                >
                                    <Target className="h-3.5 w-3.5" />
                                    Focus
                                </Link>
                            )}
                            {canSeePrerec && c.phone && (
                                <button
                                    type="button"
                                    onClick={() => setMessagingState({ category: "custom" })}
                                    data-testid="profile-send-message"
                                    title="Envoyer un SMS ou WhatsApp"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 text-[11px] font-extrabold transition shadow-btn"
                                >
                                    <MessageCircle className="h-3.5 w-3.5" />
                                    Message
                                </button>
                            )}
                            {/* Onboarding express — one-click validate + send
                                the onboarding invite. Ideal when the candidate
                                is already known (referred, ex-Shariti, walk-in).
                                Auto-hidden for hired/rejected candidates. */}
                            {canSeePrerec && (
                                <FastTrackButton
                                    candidate={c}
                                    variant="chip"
                                    onDone={() => load()}
                                    dataTestid="profile-fast-track"
                                />
                            )}
                            {/* Escape hatch: pull the candidate OUT of the
                                embauche funnel (wrong invite, mistake…). Only
                                shown for admins on candidates that are
                                actually in the funnel. */}
                            <RemoveFromOnboardingButton
                                candidate={c}
                                onDone={() => load()}
                                dataTestid="profile-remove-from-onboarding"
                            />
                            {/* Iter101 CEO 2026-09-08 : contre-partie de
                                Retirer du circuit — permet de ré-inclure
                                immédiatement un candidat qui a disparu du
                                pipeline Embauche à cause d'un flag oublié. */}
                            <ForceIncludeInPipelineButton
                                candidate={c}
                                onDone={() => load()}
                                dataTestid="profile-force-include"
                            />
                        </div>
                        <p className="mt-1 text-sm font-semibold text-slate-500 flex items-center gap-2 flex-wrap">
                            <span>{c.position}</span>
                            <span className="text-slate-300">·</span>
                            {c.source === "Site Shariti" ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-shariti-orange-soft border border-shariti-orange/30 text-shariti-orange text-[10px] font-extrabold uppercase tracking-widest" data-testid="profile-source-badge">
                                    🌐 Site Shariti
                                </span>
                            ) : (
                                <span>{c.source}</span>
                            )}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {(c.badges || []).map((b) => <SmartBadge key={b} type={b} size="sm" />)}
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 text-xs font-semibold text-slate-600">
                            {c.phone ? (
                                <Info icon={Phone} value={c.phone} href={`tel:${c.phone}`} />
                            ) : (
                                <PhoneAddField onSave={async (val) => {
                                    try {
                                        await api.patch(`/candidates/${id}`, { phone: val });
                                        toast.success("Téléphone enregistré ✅");
                                        await load();
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || "Échec de la mise à jour");
                                    }
                                }} />
                            )}
                            <Info icon={Mail} value={c.email} href={c.email ? `mailto:${c.email}` : null} />
                            <Info icon={MapPin} value={c.city} />
                            <Info icon={Sparkles} value={`Dispo: ${c.availability || "—"}`} />
                        </div>
                        {canSeePrerec && (c.call_attempts > 0 || c.to_recall) && (
                            <div className="mt-3 inline-flex items-center gap-2 flex-wrap rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-bold text-amber-800" data-testid="profile-call-state">
                                <PhoneOff className="h-3.5 w-3.5" />
                                {c.call_attempts || 0} tentative{c.call_attempts > 1 ? "s" : ""} sans réponse
                                {c.to_recall && <span className="text-[10px] font-extrabold uppercase tracking-widest text-shariti-orange ml-1">· À RAPPELER</span>}
                                {c.call_attempts > 0 && (
                                    <button
                                        onClick={handleUndoNoAnswer}
                                        data-testid="profile-undo-no-answer"
                                        className="ml-2 inline-flex items-center gap-1 text-[11px] font-extrabold text-shariti-orange hover:text-shariti-black transition underline underline-offset-2"
                                    >
                                        <Undo2 className="h-3 w-3" />
                                        Annuler le dernier
                                    </button>
                                )}
                                {c.call_attempts > 0 && c.phone && (
                                    <button
                                        onClick={() => setMessagingState({ category: "no_answer" })}
                                        data-testid="profile-send-no-answer-sms"
                                        className="inline-flex items-center gap-1 text-[11px] font-extrabold text-emerald-700 hover:text-emerald-900 transition underline underline-offset-2"
                                    >
                                        <MessageCircle className="h-3 w-3" />
                                        Envoyer SMS
                                    </button>
                                )}
                                {c.call_attempts > 1 && (
                                    <button
                                        onClick={handleResetAttempts}
                                        data-testid="profile-reset-attempts"
                                        className="inline-flex items-center gap-1 text-[11px] font-extrabold text-shariti-red hover:text-shariti-black transition underline underline-offset-2"
                                    >
                                        <Undo2 className="h-3 w-3" />
                                        Tout annuler
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="shrink-0 flex flex-col items-center gap-3">
                        <ScoreRing score={c.ai_score} size={108} stroke={7} />
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Confiance IA : {c.ai_analysis?.confidence ?? 70}%
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap gap-2">
                    {/* Revert to previous step — visible to everyone (not just admin),
                        because a pre-recruiter who clicked by mistake must be able to
                        recover instantly. Falls back to canonical pipeline on the backend
                        when no status_history is available. */}
                    {c.status !== "new" && c.status !== "prerec_assigned" && c.status !== "rejected" && (
                        <button
                            onClick={() => askStatusChange(null, {
                                title: "Revenir à l'étape précédente ?",
                                body: (
                                    <>
                                        Le candidat <strong>{c.first_name} {c.last_name}</strong> repassera à l'étape précédente du pipeline. L'action est tracée dans l'historique.
                                    </>
                                ),
                                tone: "slate",
                                confirmLabel: "Revenir en arrière",
                                via: "revert_endpoint",
                            })}
                            data-testid="profile-revert-step"
                            title="Revenir à l'étape précédente du pipeline"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange rounded-full font-semibold px-5 py-2.5 text-sm transition"
                        >
                            <Undo2 className="h-4 w-4" /> Revenir à l'étape précédente
                        </button>
                    )}
                    {canSeePrerec && (
                        <Link
                            to={`/candidats/${c.id}/scorecard/prerec`}
                            data-testid="profile-action-prerec"
                            className="btn-shariti"
                        >
                            <ClipboardList className="h-4 w-4" /> Scorecard Pré-rec
                        </Link>
                    )}
                    {canSeeImmersion && !isTerminalStatus && (
                        <Link
                            to={`/candidats/${c.id}/scorecard/immersion`}
                            data-testid="profile-action-immersion"
                            className={canSeePrerec ? "btn-secondary" : "btn-shariti"}
                        >
                            <Trophy className="h-4 w-4" /> Scorecard Immersion
                        </Link>
                    )}
                    {/* When the immersion is already done / validated /
                        onboarding / hired, the field referent has (normally)
                        filled their scorecard. Admin and pre-recruteur need
                        a 1-click way to consult the referent's evaluation
                        directly from the profile — otherwise they had to
                        dig into Récap 360°. Pure read-only access. */}
                    {canSeeImmersion && isTerminalStatus && c.status !== "rejected" && c.status !== "new" && c.status !== "prerec_assigned" && (
                        <Link
                            to={`/candidats/${c.id}/scorecard/immersion`}
                            data-testid="profile-view-immersion-scorecard"
                            className="btn-secondary"
                            title="Consulter la scorecard remplie par le référent terrain"
                        >
                            <Trophy className="h-4 w-4" /> Voir Scorecard Terrain
                        </Link>
                    )}
                    {/* Show the schedule button ONLY when no immersion is currently
                        booked. When the candidate is at immersion_scheduled, the big
                        ImmersionPlanCard below (with Reprogrammer / Annuler buttons)
                        replaces this generic action. */}
                    {canSeePrerec && !isTerminalStatus && c.status !== "immersion_scheduled" && (
                        <button
                            onClick={() => setSchedulingImmersion(true)}
                            data-testid="profile-schedule-immersion"
                            className="btn-secondary"
                        >
                            <MapPin className="h-4 w-4" /> Programmer immersion
                        </button>
                    )}
                    {canSeePrerec && (
                        <button onClick={handleRescore} data-testid="profile-rescore" className="btn-ghost">
                            <RefreshCw className={`h-4 w-4 ${rescoring ? "animate-spin" : ""}`} /> Re-scorer IA
                        </button>
                    )}
                    {/* Qualifier — opens a multi-action modal letting the user pick
                        WHY the call didn't move forward: pas de réponse, candidat plus
                        disponible, refus définitif, vivier, ou programmer l'immersion. */}
                    {canSeePrerec && !isTerminalStatus && ["new", "prerec_assigned"].includes(c.status) && (
                        <button
                            onClick={() => setShowCallActionModal(true)}
                            data-testid="profile-qualify"
                            title="Qualifier l'issue de l'appel"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 border border-shariti-gray hover:text-shariti-orange hover:border-shariti-orange rounded-full font-semibold px-5 py-2.5 text-sm transition"
                        >
                            <PhoneOff className="h-4 w-4" />
                            Qualifier
                        </button>
                    )}
                    {(hasRole(user, "admin") || hasRole(user, "referent")) && c.status === "immersion_done" && (
                        <button onClick={() => askStatusChange("validated", {
                            title: "Valider le recrutement ?",
                            body: (
                                <>
                                    Vous recommandez officiellement <strong>{c.first_name} {c.last_name}</strong> pour le poste. Le candidat passera au statut <strong>« Validé »</strong> et un onboarding pourra être déclenché.
                                </>
                            ),
                            tone: "green",
                            confirmLabel: "Valider le recrutement",
                        })} data-testid="profile-validate" className="btn-shariti">
                            <CheckCircle2 className="h-4 w-4" /> Valider recrutement
                        </button>
                    )}
                    {(hasRole(user, "admin") || hasRole(user, "referent")) && c.status === "validated" && !c.onboarding_form_id && (
                        <button onClick={async () => {
                            try {
                                await api.post(`/onboarding/candidates/${id}/send`);
                                toast.success("Dossier d'embauche envoyé au candidat ✅");
                                await load();
                            } catch (e) {
                                toast.error(e.response?.data?.detail || "Erreur");
                            }
                        }} data-testid="profile-onboarding" className="btn-shariti">
                            <Send className="h-4 w-4" /> Envoyer le dossier d'embauche
                        </button>
                    )}
                    {!c.in_talent_pool && c.status !== "rejected" && c.status !== "hired" && (
                        <button
                            onClick={() => {
                                // Suggest a smart default reason based on the candidate's current state
                                let defaultReason = "cant_reach";
                                if (c.immersion_no_show) defaultReason = "immersion_noshow";
                                else if (!c.call_attempts) defaultReason = "to_recontact_later";
                                setTalentPoolModal({ defaultReason });
                            }}
                            data-testid="profile-talent-pool"
                            className="inline-flex items-center justify-center gap-2 bg-white text-amber-700 border border-amber-200 hover:bg-amber-50 rounded-full font-semibold px-5 py-2.5 text-sm transition"
                        >
                            <BookmarkPlus className="h-4 w-4" /> Mettre au vivier
                        </button>
                    )}
                    {c.in_talent_pool && (
                        <button
                            onClick={async () => {
                                try {
                                    await api.post(`/candidates/${id}/talent-pool/reactivate`);
                                    toast.success(`${c.first_name} a été réactivé(e) ✅`);
                                    window.dispatchEvent(new CustomEvent("vivier:changed"));
                                    await load();
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || "Action impossible");
                                }
                            }}
                            data-testid="profile-reactivate-from-pool"
                            className="inline-flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-full font-semibold px-5 py-2.5 text-sm transition"
                        >
                            <Undo2 className="h-4 w-4" /> Réactiver du vivier
                        </button>
                    )}
                    {c.status === "immersion_scheduled" && (
                        <button
                            onClick={async () => {
                                try {
                                    await api.patch(`/candidates/${id}/no-relance`, { no_relance: !c.no_relance });
                                    toast.success(
                                        c.no_relance
                                            ? "Relances automatiques réactivées"
                                            : "Plus de relance automatique pour ce candidat"
                                    );
                                    await load();
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || "Action impossible");
                                }
                            }}
                            data-testid="profile-toggle-no-relance"
                            title={c.no_relance
                                ? "Réactiver les relances H+24 / H+48 / J-1"
                                : "Désactiver les relances automatiques pour ce candidat"}
                            className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold px-5 py-2.5 text-sm transition border ${
                                c.no_relance
                                    ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                                    : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                            }`}
                        >
                            {c.no_relance ? (
                                <>
                                    <BellOff className="h-4 w-4" /> Relances coupées
                                </>
                            ) : (
                                <>
                                    <BellRing className="h-4 w-4" /> Auto-relance ON
                                </>
                            )}
                        </button>
                    )}
                    {!isTerminalStatus && (
                        <button
                            onClick={() => {
                                // iter97 · Open the reason picker BEFORE the reject.
                                // Post-immersion refusals go through the dedicated
                                // PostImmersionDecisionModal (which already has
                                // its own set of granular reasons).
                                if (c.status === "immersion_done" || c.status === "immersion_scheduled") {
                                    askStatusChange("rejected", {
                                        title: "Refuser après immersion ?",
                                        body: <><strong>{c.first_name} {c.last_name}</strong> sera marqué comme refusé après immersion.</>,
                                        tone: "red",
                                        confirmLabel: "Refuser après immersion",
                                        via: "reject_endpoint",
                                    });
                                } else {
                                    setRejectModal({ defaultReason: "" });
                                }
                            }}
                            data-testid="profile-reject"
                            className="inline-flex items-center justify-center gap-2 bg-white text-shariti-red border border-red-200 hover:bg-red-50 rounded-full font-semibold px-5 py-2.5 text-sm transition"
                        >
                            <UserX className="h-4 w-4" />
                            {c.status === "immersion_done" || c.status === "immersion_scheduled"
                                ? "Refuser après immersion"
                                : "Refuser"}
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            onClick={() => setConfirmDelete(true)}
                            data-testid="profile-hard-delete"
                            title="Supprimer définitivement (admin)"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-500 border border-shariti-gray hover:text-shariti-red hover:border-red-200 hover:bg-red-50 rounded-full font-semibold px-4 py-2.5 text-sm transition"
                        >
                            <Trash2 className="h-4 w-4" /> Supprimer
                        </button>
                    )}
                </div>
                {/* Last status change indicator + revert button
                    iter101 · CEO 2026-09-06 : masquer "Revenir en arrière"
                    quand le dernier changement était une UN-rejection
                    (rejected→X). Sinon le clic ferait basculer le candidat
                    BACK to rejected — c'était le piège qui a bloqué Comlan.
                    Le CTA vert "Annuler le refus & reprendre" (FunnelStrip)
                    reste seul point d'entrée pour ce cas. */}
                {lastStatusChange && lastStatusChange.from_status !== "rejected" && (
                    <div
                        className="mt-4 flex items-center gap-2 flex-wrap rounded-2xl bg-shariti-cream border border-shariti-gray px-3 py-2 text-xs font-semibold text-slate-600"
                        data-testid="profile-last-status-change"
                    >
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Dernier changement
                        </span>
                        <span>
                            {lastStatusChange.from_status || "—"}
                            <span className="mx-1.5 text-slate-400">→</span>
                            <strong className="text-shariti-black">{lastStatusChange.to_status}</strong>
                        </span>
                        <span className="text-[10px] font-medium text-slate-400">
                            par {lastStatusChange.by_name || "—"}
                        </span>
                        <button
                            onClick={() => askStatusChange(null, {
                                title: "Annuler le dernier changement de statut ?",
                                body: (
                                    <>
                                        Le candidat repassera de <strong>« {lastStatusChange.to_status} »</strong> à <strong>« {lastStatusChange.from_status || "—"} »</strong>.
                                    </>
                                ),
                                tone: "slate",
                                confirmLabel: "Revenir en arrière",
                                via: "revert_endpoint",
                            })}
                            data-testid="profile-revert-status"
                            className="ml-auto inline-flex items-center gap-1 text-[11px] font-extrabold text-shariti-orange hover:text-shariti-black transition underline underline-offset-2"
                        >
                            <Undo2 className="h-3 w-3" />
                            Revenir en arrière
                        </button>
                    </div>
                )}
            </motion.div>

            {/* Surface the last meaningful decision (refus / validation /
                immersion faite / no-show) at the very top — admin and pre-rec
                must see *who* / *when* / *why* at a glance, without clicking
                "Voir la fiche" or hunting in the timeline. Same wording as
                the calendar drawer. */}
            {lastDecision && (
                <LastDecisionBanner
                    decision={lastDecision}
                    className="md:rounded-3xl"
                />
            )}

            {/* iter101 · CEO 2026-09-07 : « après avoir cliqué Annuler le refus,
                je veux voir une trace claire "Refusé par X le Y — Refus annulé
                par Z le W" plutôt que le refus qui disparaît sans laisser de
                mention. » Bloc visible UNIQUEMENT quand le candidat n'est
                plus rejeté ET qu'il a été rejeté dans son historique. */}
            <RejectionAuditTrail candidate={c} />

            {/* When the candidate has a programmed immersion, surface a big
                contextual card with all the details + Reprogrammer / Annuler
                actions. Replaces the generic "Programmer immersion" button. */}
            {c.status === "immersion_scheduled" && c.immersion_scheduled_at && (
                <ImmersionPlanCard
                    candidate={c}
                    onReschedule={() => setSchedulingImmersion(true)}
                    onCancel={() => setCancellingImmersion(true)}
                    onDecide={() => setDecidingImmersion(true)}
                />
            )}

            {/* Once the candidate is validated (or already onboarding), show the
                onboarding dossier card — its own component fetches the form,
                docs, and exposes admin actions (Approve / Ask-fix / Resend).
                iter101 · Toujours afficher la carte si le candidat a une fiche
                (`onboarding_form_id`), même quand son statut est `rejected`.
                Bloquer l'accès à un dossier déjà soumis empêchait la RH
                d'approuver un candidat qui avait changé d'avis (CEO 2026-09-06). */}
            {(["validated", "onboarding", "hired"].includes(c.status) || c.onboarding_form_id) && (
                <div id="onboarding" className="scroll-mt-24">
                    <OnboardingCard candidate={c} user={user} />
                </div>
            )}

            {/* Contract Card — Phase B — visible from `validated` onwards for
                the standard template flow, PLUS earlier statuses so admin can
                use the RETROACTIVE onboarding (CDI/CDD déjà signé) block
                without having to force the candidate through the funnel
                (CEO brief 2026-08-03 : "je veux onboarder ce CDI rétroactivement
                sans que ça bug ni bloque").
                iter101 · Un candidat rejeté avec une fiche soumise doit
                pouvoir avancer jusqu'au contrat si la RH annule le refus —
                on affiche donc aussi la carte s'il y a une fiche (le
                composant gère l'état vide en interne). */}
            {c.status && ![
                "trashed",
            ].includes(c.status) && (c.status !== "rejected" || c.onboarding_form_id) && (
                <div id="contract" className="scroll-mt-24 space-y-3">
                    {/* iter110n · CEO 2026-09-23 · Bouton 1-clic « Rupture
                        période d'essai » — visible juste au-dessus de la
                        carte contrat. Le backend enforce la présence d'un
                        contrat signé (400 sinon) donc on affiche toujours
                        pour les admins/coords : le bouton se rend simplement
                        invisible pour les rôles sans permission et le
                        backend refuse proprement pour les candidats sans
                        contrat signé. */}
                    <div className="flex items-center justify-end gap-2">
                        <EndTrialButton candidate={c} onSent={load} />
                    </div>
                    <ContractCard candidate={c} />
                </div>
            )}

            {/* Paie & terrain — matricule (code RD), plafond horaire, statut
                actif_terrain. Rendered from `hired` onwards (the moment
                a candidate becomes a collaborateur — the signature hook
                stamps `code_rd` at this stage). Admin-only editing. */}
            {c.status === "hired" && (
                <PaieTerrainCard candidate={c} user={user} onChange={load} />
            )}

            {/* iter110 · Badge collaborateur — visible dès qu'on a passé l'embauche */}
            {c.status === "hired" && (
                <BadgeCard candidate={c} onChange={load} />
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left column: AI analysis */}
                <div className="lg:col-span-2 space-y-6">
                {/* Synthèse IA — full version for prerec/admin, light version for referent */}
                {canSeePrerec ? (
                    <div className="card-soft p-6 md:p-7" data-testid="profile-ai-summary">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-shariti-orange" />
                            <h3 className="text-lg font-extrabold tracking-tight">Synthèse IA</h3>
                        </div>
                        <p className="text-sm font-medium text-slate-600 leading-relaxed">
                            {c.ai_analysis?.summary || "Analyse en cours."}
                        </p>
                        {c.ai_analysis?.recommendation && (
                            <div className="mt-4 p-4 rounded-2xl bg-shariti-orange-soft border border-shariti-orange/20">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-1">Recommandation</div>
                                <div className="text-sm font-bold text-shariti-black">{c.ai_analysis.recommendation}</div>
                            </div>
                        )}

                        <div className="mt-6 grid md:grid-cols-2 gap-4">
                            <ListBlock title="Points forts" color="text-emerald-600" icon={CheckCircle2} items={c.ai_analysis?.strengths || []} />
                            <ListBlock title="Points vigilance" color="text-amber-600" icon={AlertTriangle} items={c.ai_analysis?.vigilance || []} />
                        </div>

                        <div className="mt-6">
                            <ListBlock title="Questions prioritaires à poser" color="text-shariti-orange" icon={Lightbulb} items={c.ai_analysis?.priority_questions || []} />
                        </div>
                    </div>
                ) : (
                    /* Referent view: minimal context — no AI breakdown to avoid bias.
                       Only neutral summary + valorised signals (objective facts from CV). */
                    <div className="card-soft p-6 md:p-7" data-testid="profile-referent-context">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-shariti-orange" />
                            <h3 className="text-lg font-extrabold tracking-tight">Contexte terrain</h3>
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">
                                Vue référent
                            </span>
                        </div>
                        <p className="text-sm font-medium text-slate-600 leading-relaxed mb-4">
                            {c.ai_analysis?.summary || "Pas de synthèse disponible."}
                        </p>
                        {(c.ai_analysis?.valued_signals || []).length > 0 && (
                            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 mb-4">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-2">
                                    ✨ Signaux objectifs détectés dans le CV
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(c.ai_analysis?.valued_signals || []).map((s, i) => (
                                        <span
                                            key={i}
                                            className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border border-emerald-300 text-emerald-700"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="text-[11px] font-medium text-slate-400 italic">
                            Évaluez le candidat librement sur sa performance terrain — la pré-qualification téléphonique est volontairement masquée pour préserver l'objectivité de votre observation.
                        </div>
                    </div>
                )}

                    {canSeePrerec && (
                        <div className="card-soft p-6 md:p-7" data-testid="profile-sub-scores">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-extrabold tracking-tight">Scores détaillés IA</h3>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">/ 100</span>
                            </div>
                            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                                {Object.entries(SUB_SCORE_LABELS).map(([key, label]) => {
                                    const value = subScores[key] || 0;
                                    const isRisk = key === "risque_attrition";
                                    return (
                                        <div key={key}>
                                            <div className="flex items-center justify-between text-xs font-bold mb-1.5">
                                                <span className="text-slate-600">{label}</span>
                                                <span className={isRisk && value > 50 ? "text-shariti-red" : "text-shariti-black"}>{value}</span>
                                            </div>
                                            <div className="relative w-full h-2 bg-shariti-gray rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${value}%` }}
                                                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                                    className={`absolute inset-y-0 left-0 rounded-full ${
                                                        isRisk ? "bg-gradient-to-r from-amber-400 to-red-500" : "bg-gradient-to-r from-shariti-yellow to-shariti-orange"
                                                    }`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Explainability panel — only for prerec/admin (referent qualifies without bias) */}
                    {canSeePrerec && (
                        <ScoreBreakdown
                            detail={c.ai_analysis?.sub_scores_detail || {}}
                            subScores={subScores}
                            valuedSignals={c.ai_analysis?.valued_signals || []}
                            candidateId={c.id}
                            onRescored={load}
                        />
                    )}

                    <div className="card-soft p-6 md:p-7" data-testid="profile-history">
                        <h3 className="text-lg font-extrabold tracking-tight mb-4">Historique des évaluations</h3>
                        {data.scorecards?.length === 0 && (
                            <div className="text-sm font-semibold text-slate-400 italic">Aucune scorecard remplie.</div>
                        )}
                        <div className="space-y-4">
                            {data.scorecards?.map((s) => (
                                <EvaluationCard key={s.id} scorecard={s} criteriaMap={criteriaMap} />
                            ))}
                        </div>
                    </div>

                    {/* Complete action timeline — every status change (refus,
                        validation, programmation, no-show…) with date, hour,
                        author, motif and free-form comment. Visible at the
                        bottom of the profile so admin/pre-rec can audit
                        without leaving the page. */}
                    <ActionTimeline candidate={c} />
                </div>

                {/* Right column: meta */}
                <div className="space-y-6">
                    {/* Communications history */}
                    {canSeePrerec && (
                        <div className="card-soft p-6" data-testid="profile-message-history-card">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <h3 className="text-sm font-extrabold uppercase tracking-widest text-shariti-orange flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4" />
                                    Communications
                                </h3>
                                {c.phone && (
                                    <button
                                        type="button"
                                        onClick={() => setMessagingState({ category: "custom" })}
                                        data-testid="profile-history-send"
                                        className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-600 hover:text-emerald-800 transition"
                                    >
                                        + Envoyer
                                    </button>
                                )}
                            </div>
                            <MessageHistory candidateId={c.id} refreshTick={historyTick} />
                        </div>
                    )}

                    {/* Call center — Twilio native calling history for this candidate.
                        Shows every outbound click-to-call + inbound + voicemail
                        with audio playback, Deepgram transcription and Claude
                        AI summary. Only visible to roles that can see the
                        pre-rec funnel (admin + pré-recruteur). */}
                    {canSeePrerec && (
                        <div className="card-soft p-6" data-testid="profile-call-history-card">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <h3 className="text-sm font-extrabold uppercase tracking-widest text-shariti-orange flex items-center gap-2">
                                    <Phone className="h-4 w-4" />
                                    Appels PREREC
                                </h3>
                                {c.phone && (
                                    <CallButton
                                        candidate={c}
                                        variant="wide"
                                        dataTestid="profile-call-button"
                                    />
                                )}
                            </div>
                            <CallHistory candidateId={c.id} />
                        </div>
                    )}
                    {c.immersion_scheduled_at && ["immersion_scheduled"].includes(c.status) && (
                        <div className="card-soft p-6 bg-shariti-orange-soft/40 border-shariti-orange/20" data-testid="profile-immersion-card">
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin className="h-4 w-4 text-shariti-orange" />
                                <h3 className="text-sm font-extrabold uppercase tracking-widest text-shariti-orange">
                                    Immersion programmée
                                </h3>
                            </div>
                            <div className="text-base font-extrabold text-shariti-black">
                                {new Date(c.immersion_scheduled_at).toLocaleDateString("fr-FR", {
                                    timeZone: "Europe/Paris",
                                    weekday: "long", day: "numeric", month: "long",
                                })}
                            </div>
                            <div className="text-sm font-bold text-slate-600 mt-0.5">
                                {new Date(c.immersion_scheduled_at).toLocaleTimeString("fr-FR", {
                                    timeZone: "Europe/Paris",
                                    hour: "2-digit", minute: "2-digit",
                                })}
                                {c.immersion_location && <span className="text-slate-400 font-medium"> · {c.immersion_location}</span>}
                            </div>
                            {c.immersion_notes && (
                                <p className="mt-3 text-xs font-medium text-slate-600 italic border-l-2 border-shariti-orange/30 pl-2">
                                    {c.immersion_notes}
                                </p>
                            )}
                            <ImmersionEngagementBadge candidate={c} />
                            {canSeePrerec && (
                                <button
                                    onClick={() => setSchedulingImmersion(true)}
                                    data-testid="profile-reschedule-immersion"
                                    className="mt-3 text-[11px] font-extrabold text-shariti-orange hover:text-shariti-black underline underline-offset-2 transition"
                                >
                                    Modifier la planification
                                </button>
                            )}
                        </div>
                    )}
                    {isAdmin && <RegionReassignCard candidate={c} onChanged={load} />}
                    {c.layan_offer_title && (
                        <div className="card-soft p-6" data-testid="profile-offer-block">
                            <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-400 mb-3">
                                Offre source
                            </h3>
                            <Link
                                to={`/candidats?offer_id=${c.layan_offer_id}`}
                                className="text-base font-extrabold tracking-tight text-shariti-black hover:text-shariti-orange transition block"
                                title="Voir tous les candidats de cette offre"
                            >
                                {c.layan_offer_title}
                                {c.layan_offer_region && (
                                    <span
                                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange text-[10px] font-black uppercase tracking-widest border border-shariti-orange/30 align-middle"
                                        data-testid="profile-offer-region"
                                    >
                                        {c.layan_offer_region}
                                    </span>
                                )}
                            </Link>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {c.layan_offer_status && (
                                    <span
                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                            c.layan_offer_status === "Publiée"
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : c.layan_offer_status === "Brouillon"
                                                ? "bg-amber-50 text-amber-800 border-amber-200"
                                                : "bg-slate-100 text-slate-600 border-slate-200"
                                        }`}
                                    >
                                        Offre {c.layan_offer_status}
                                    </span>
                                )}
                                {c.application_status && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/20">
                                        {c.application_status}
                                    </span>
                                )}
                            </div>
                            {c.applied_at && (
                                <p className="mt-3 text-xs font-medium text-slate-500">
                                    Candidature reçue le {formatDateTime(c.applied_at)}
                                </p>
                            )}
                            {c.reject_reason && (
                                <p className="mt-1 text-xs font-bold text-shariti-red">
                                    Motif rejet : {c.reject_reason}
                                </p>
                            )}
                        </div>
                    )}
                    {c.source === "Site Shariti" && (
                        <div className="card-soft p-6" data-testid="profile-site-application-block">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">🌐</span>
                                <h3 className="text-sm font-extrabold uppercase tracking-widest text-shariti-orange">
                                    Candidature site Shariti
                                </h3>
                            </div>
                            {c.applied_at && (
                                <p className="text-xs font-bold text-slate-500 mb-3">
                                    Reçue le {formatDateTime(c.applied_at)}
                                </p>
                            )}
                            {c.application_message && (
                                <div className="mb-3">
                                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">Message du candidat</div>
                                    <p className="text-sm font-medium text-slate-700 leading-relaxed whitespace-pre-line border-l-2 border-shariti-orange/30 pl-3" data-testid="profile-application-message">
                                        {c.application_message}
                                    </p>
                                </div>
                            )}
                            {c.additional_info && (
                                <div className="mb-3">
                                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">Informations complémentaires</div>
                                    <p className="text-sm font-medium text-slate-700">{c.additional_info}</p>
                                </div>
                            )}
                            {c.address && (
                                <div className="mb-3">
                                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1">Adresse postale</div>
                                    <p className="text-sm font-medium text-slate-700">{c.address}</p>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3">
                                {c.newsletter_consent && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        <Check className="h-3 w-3" /> Inscrit newsletter
                                    </span>
                                )}
                                {(c.cv_url || c.cv_pdf_data) && (
                                    <a
                                        href={c.cv_pdf_data || c.cv_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download={c.cv_pdf_data ? `CV-${c.first_name}-${c.last_name}.pdf` : undefined}
                                        data-testid="profile-cv-link"
                                        className="inline-flex items-center gap-1.5 rounded-full bg-shariti-black hover:bg-slate-800 text-white px-3 py-1 text-[11px] font-extrabold transition"
                                    >
                                        <FileDown className="h-3 w-3" />
                                        {c.cv_pdf_data ? "Télécharger le CV" : "Voir le CV"}
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="card-soft p-6">
                        <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-400 mb-3">Profil dominant</h3>
                        <div className="text-2xl font-black tracking-tight text-shariti-black">
                            {PROFILE_META[c.profile_dominant] || "À déterminer"}
                        </div>
                        <p className="mt-2 text-xs font-medium text-slate-500">Détecté par l'IA à partir du CV et de la synthèse.</p>
                    </div>
                    <div className="card-soft p-6">
                        <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-400 mb-3">Scoring global</h3>
                        <div className="space-y-3 text-sm font-semibold">
                            <Row label="Pré-recrutement" value={c.prerec_percent ? `${c.prerec_percent}%` : "—"} />
                            <Row label="Immersion terrain" value={c.immersion_percent ? `${c.immersion_percent}%` : "—"} />
                            <Row label="Score IA initial" value={`${c.ai_score}/100`} />
                            <Row label="Score global" value={c.global_score ? `${c.global_score}` : "—"} bold />
                        </div>
                    </div>
                    <div className="card-soft p-6">
                        <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-400 mb-3">Logistique</h3>
                        <div className="space-y-3 text-sm font-semibold">
                            <Row label="Disponibilité" value={c.availability || "—"} />
                            <Row label="Mobilité" value={c.mobility || "—"} />
                            <Row label="Source" value={c.source || "Spontanée"} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm modal for status changes (programmer immersion / valider / refuser / etc.) */}
            {pendingStatusChange && (
                <ConfirmModal
                    title={pendingStatusChange.title}
                    body={pendingStatusChange.body}
                    confirmLabel={pendingStatusChange.confirmLabel}
                    tone={pendingStatusChange.tone}
                    icon={pendingStatusChange.tone === "red" ? AlertTriangle : CheckCircle2}
                    onCancel={() => setPendingStatusChange(null)}
                    onSecondary={
                        // iter70 — CEO 2026-07-31 : offrir l'échappement
                        // « Ou plutôt Vivier » sur toute confirmation de rejet.
                        pendingStatusChange.via === "reject_endpoint"
                            && !c.in_talent_pool
                            && c.status !== "hired"
                            ? () => {
                                const reason = pendingStatusChange.reason || "cant_reach";
                                setPendingStatusChange(null);
                                setTalentPoolModal({ defaultReason: reason });
                            }
                            : undefined
                    }
                    secondaryLabel="Ou plutôt vivier"
                    onConfirm={async () => {
                        const change = pendingStatusChange;
                        setPendingStatusChange(null);
                        if (change.via === "reject_endpoint") {
                            await handleReject(change.reason);
                        } else if (change.via === "revert_endpoint") {
                            await handleRevertStatus();
                        } else if (change.status) {
                            await setStatusSilently(change.status);
                        }
                    }}
                />
            )}

            {/* Identity correction — typo fixer that preserves code_rd */}
            {editingIdentity && (
                <EditIdentityModal
                    candidate={c}
                    onClose={() => setEditingIdentity(false)}
                    onSaved={load}
                />
            )}

            {/* OCR pièce d'identité — Claude Sonnet 4.5 vision reads the
                CNI/titre de séjour/passeport and lets admin one-click
                apply the extracted values to the candidate profile. */}
            {idOcrOpen && (
                <IdOcrModal
                    candidateId={c.id}
                    onClose={() => setIdOcrOpen(false)}
                    onSaved={load}
                />
            )}

            {/* Hard delete modal — admin only, irreversible */}
            {confirmDelete && (
                <ConfirmModal
                    testid="confirm-hard-delete"
                    title="Supprimer définitivement le candidat ?"
                    body={
                        <>
                            <p>
                                Vous êtes sur le point de supprimer <strong>{c.first_name} {c.last_name}</strong> de manière définitive.
                            </p>
                            <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-shariti-red">
                                <strong>⚠️ Cette action est irréversible.</strong> Elle supprimera définitivement&nbsp;:
                                <ul className="mt-1.5 ml-4 list-disc space-y-0.5">
                                    <li>la fiche candidat,</li>
                                    <li>toutes ses scorecards (pré-rec + immersion),</li>
                                    <li>tous les messages SMS / WhatsApp envoyés,</li>
                                    <li>les notifications associées.</li>
                                </ul>
                            </div>
                        </>
                    }
                    confirmLabel="Supprimer définitivement"
                    cancelLabel="Annuler"
                    tone="red"
                    icon={AlertTriangle}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={handleHardDelete}
                />
            )}

            {/* Talent pool modal — park candidate in the "vivier" for future re-engagement */}
            {talentPoolModal && (
                <TalentPoolModal
                    candidate={c}
                    defaultReason={talentPoolModal.defaultReason}
                    onClose={() => setTalentPoolModal(null)}
                    onParked={async () => {
                        // Once a candidate is in the vivier they no longer
                        // belong to the active call queue. Pull them out of
                        // the persisted queue so prev/next navigation skips
                        // them, and auto-jump to the neighbour if we were
                        // navigating in a queue context.
                        const neighbourId = removeFromQueue(id);
                        toast.success(`${c.first_name} déplacé(e) dans le vivier — retiré de la file`);
                        if (neighbourId) {
                            navigate(`/candidats/${neighbourId}`);
                        } else {
                            await load();
                        }
                    }}
                />
            )}

            {/* Multi-action "Qualifier" modal — let the user pick the outcome of the call:
                Programmer immersion / Pas de réponse / Plus dispo / Refus / Vivier.
                Same component used inside the scorecard sticky bar. */}
            {showCallActionModal && (
                <CallActionModal
                    candidate={c}
                    callAttempts={c.call_attempts || 0}
                    onClose={() => setShowCallActionModal(false)}
                    onSchedule={() => {
                        setShowCallActionModal(false);
                        setSchedulingImmersion(true);
                    }}
                    onNoAnswer={async () => {
                        setShowCallActionModal(false);
                        if (qualifying) return;
                        setQualifying(true);
                        try {
                            await api.post(`/candidates/${id}/no-answer`);
                            toast.success("Candidat marqué « appelé · sans réponse »");
                            await load();
                            setMessagingState({ category: "no_answer" });
                        } catch (e) {
                            toast.error(e.response?.data?.detail || "Impossible de qualifier");
                        } finally {
                            setQualifying(false);
                        }
                    }}
                    onArchiveUnavailable={() => {
                        setShowCallActionModal(false);
                        askStatusChange("rejected", {
                            title: "Candidat plus disponible ?",
                            body: <>Le candidat <strong>{c.first_name}</strong> sera archivé comme indisponible. Il pourra être recontacté plus tard depuis l'onglet Refusés.</>,
                            tone: "orange",
                            confirmLabel: "Archiver",
                            via: "reject_endpoint",
                            reason: "candidat_indisponible",
                        });
                    }}
                    onRejectDefinitive={() => {
                        setShowCallActionModal(false);
                        askStatusChange("rejected", {
                            title: "Refus définitif ?",
                            body: <><strong>{c.first_name} {c.last_name}</strong> sera marqué comme refusé et sortira définitivement de toutes les files d'appels.</>,
                            tone: "red",
                            confirmLabel: "Refuser définitivement",
                            via: "reject_endpoint",
                            reason: "criteres_non_remplis",
                        });
                    }}
                    onUnreachable={() => {
                        setShowCallActionModal(false);
                        askStatusChange("rejected", {
                            title: "Numéro injoignable ?",
                            body: <>Après plusieurs tentatives, le numéro de <strong>{c.first_name}</strong> reste inaccessible. Sortir le candidat de la file ?</>,
                            tone: "red",
                            confirmLabel: "Marquer injoignable",
                            via: "reject_endpoint",
                            reason: "numero_injoignable",
                        });
                    }}
                    onTalentPool={() => {
                        setShowCallActionModal(false);
                        setTalentPoolModal({ defaultReason: "cant_reach" });
                    }}
                />
            )}

            {schedulingImmersion && (
                <ScheduleImmersionModal
                    candidate={c}
                    onCancel={() => setSchedulingImmersion(false)}
                    onSuccess={async (_updatedCandidate, opts) => {
                        setSchedulingImmersion(false);
                        await load();
                        // Open the WhatsApp recap modal automatically when the user
                        // asked for it (default ON) so the candidate is
                        // immediately notified of the new date/lieu/référent.
                        if (c.phone && opts?.notifyAfter !== false) {
                            setMessagingState({ category: "recap_interview" });
                        }
                    }}
                />
            )}

            {messagingState && (
                <SendMessageModal
                    candidate={c}
                    defaultCategory={messagingState.category}
                    onClose={() => setMessagingState(null)}
                    onSent={() => setHistoryTick((t) => t + 1)}
                />
            )}

            {/* Cancel-immersion confirmation modal (triggered from ImmersionPlanCard) */}
            {cancellingImmersion && (
                <ConfirmModal
                    open
                    title="Annuler cette immersion ?"
                    body={
                        <>
                            L&apos;immersion de <strong>{c.first_name} {c.last_name}</strong> sera annulée. Le candidat repasse à <em>Pré-rec fait</em>, le créneau du référent est libéré, et le lien magique précédent devient invalide. Vous pourrez ensuite reprogrammer proprement (référent, lieu, téléphone).
                        </>
                    }
                    tone="red"
                    confirmLabel="Confirmer l'annulation"
                    cancelLabel="Garder l'immersion"
                    onCancel={() => setCancellingImmersion(false)}
                    onConfirm={async () => {
                        try {
                            await api.post(`/candidates/${c.id}/cancel-immersion`);
                            toast.success("Immersion annulée — vous pouvez la reprogrammer");
                            setCancellingImmersion(false);
                            await load();
                        } catch (e) {
                            toast.error(e.response?.data?.detail || "Annulation impossible");
                        }
                    }}
                />
            )}

            {/* Post-immersion decision modal (Valider / Refuser) */}
            {decidingImmersion && (
                <PostImmersionDecisionModal
                    candidate={c}
                    onClose={() => setDecidingImmersion(false)}
                    onDone={async () => {
                        setDecidingImmersion(false);
                        await load();
                    }}
                />
            )}

            {/* iter97 · Force reject reason picker before every "Refus définitif" */}
            {rejectModal && (
                <RejectReasonModal
                    open={true}
                    onOpenChange={(v) => { if (!v) setRejectModal(null); }}
                    candidate={c}
                    defaultReason={rejectModal.defaultReason}
                    onConfirm={async (reason, note) => {
                        try {
                            await api.post(`/candidates/${id}/reject`, { reason, note });
                            removeFromQueue(id);
                            toast.success("Candidat refusé");
                            setRejectModal(null);
                            await load();
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "Erreur lors du refus");
                        }
                    }}
                />
            )}
        </div>
    );
}

function ImmersionEngagementBadge({ candidate }) {
    if (!candidate?.immersion_scheduled_at) return null;

    const confirmedAt = candidate.immersion_confirmed_at;
    const viewedAt = candidate.immersion_link_viewed_at;
    const hasToken = !!candidate.immersion_confirm_token;

    let state;
    if (confirmedAt) {
        state = {
            kind: "confirmed",
            icon: CheckCircle2,
            label: "Présence confirmée",
            detail: `par le candidat · ${new Date(confirmedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}`,
            classes: "bg-emerald-50 border-emerald-300/60 text-emerald-700",
            iconClass: "text-emerald-600",
        };
    } else if (viewedAt) {
        state = {
            kind: "viewed",
            icon: Eye,
            label: "Lien consulté",
            detail: `mais pas encore confirmé · ${new Date(viewedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}`,
            classes: "bg-amber-50 border-amber-300/60 text-amber-800",
            iconClass: "text-amber-600",
        };
    } else if (hasToken) {
        state = {
            kind: "pending",
            icon: Clock,
            label: "En attente de confirmation",
            detail: "le candidat n'a pas encore ouvert le lien",
            classes: "bg-slate-50 border-slate-200 text-slate-600",
            iconClass: "text-slate-400",
        };
    } else {
        return null;
    }

    const Icon = state.icon;
    return (
        <div
            className={`mt-3 flex items-start gap-2.5 rounded-xl border px-3 py-2 ${state.classes}`}
            data-testid={`immersion-engagement-${state.kind}`}
        >
            <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${state.iconClass}`} />
            <div className="min-w-0">
                <div className="text-[11px] font-extrabold uppercase tracking-wider leading-tight">{state.label}</div>
                <div className="text-[11px] opacity-80 mt-0.5 leading-snug">{state.detail}</div>
            </div>
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-slate-500">{label}</span>
            <span className={bold ? "text-shariti-orange font-black text-base" : "text-shariti-black"}>{value}</span>
        </div>
    );
}

function Info({ icon: Icon, value, href = null }) {
    const content = (
        <span className="break-all leading-tight" title={value}>
            {value}
        </span>
    );
    return (
        <div className="flex items-start gap-2 min-w-0">
            <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
            {href ? (
                <a href={href} className="hover:text-shariti-orange transition min-w-0">
                    {content}
                </a>
            ) : (
                <div className="min-w-0 flex-1">{content}</div>
            )}
        </div>
    );
}


function PhoneAddField({ onSave }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);

    async function submit() {
        const v = value.trim();
        if (!v) return;
        setSaving(true);
        try {
            await onSave(v);
            setEditing(false);
            setValue("");
        } finally {
            setSaving(false);
        }
    }

    if (!editing) {
        return (
            <button
                onClick={() => setEditing(true)}
                data-testid="phone-add-button"
                className="flex items-start gap-2 min-w-0 text-amber-700 hover:text-shariti-orange transition group"
            >
                <Phone className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <span className="break-all leading-tight underline underline-offset-2 decoration-amber-300 group-hover:decoration-shariti-orange">
                    + Ajouter le numéro
                </span>
            </button>
        );
    }
    return (
        <div className="flex items-center gap-1.5 min-w-0 col-span-1 md:col-span-2">
            <Phone className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <input
                type="tel"
                autoFocus
                placeholder="Ex: 06 12 34 56 78"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") submit();
                    if (e.key === "Escape") { setEditing(false); setValue(""); }
                }}
                disabled={saving}
                data-testid="phone-add-input"
                className="flex-1 min-w-0 h-7 px-2 rounded-lg border border-amber-300 text-xs font-bold text-shariti-black outline-none focus:border-shariti-orange"
            />
            <button
                onClick={submit}
                disabled={saving || !value.trim()}
                data-testid="phone-add-save"
                className="h-7 px-2.5 rounded-lg bg-shariti-orange text-white text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-50"
            >
                {saving ? "…" : "OK"}
            </button>
            <button
                onClick={() => { setEditing(false); setValue(""); }}
                disabled={saving}
                className="h-7 px-2 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-bold"
            >
                ✕
            </button>
        </div>
    );
}

function ListBlock({ title, color, icon: Icon, items }) {
    if (!items || items.length === 0) {
        return (
            <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2 text-slate-400">
                    <Icon className="h-3.5 w-3.5" /> {title}
                </div>
                <div className="text-xs font-semibold text-slate-400 italic">—</div>
            </div>
        );
    }
    return (
        <div>
            <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2 ${color}`}>
                <Icon className="h-3.5 w-3.5" /> {title}
            </div>
            <ul className="space-y-1.5 text-sm font-medium text-slate-600">
                {items.map((it, i) => (
                    <li key={i} className="flex gap-2"><span className="text-slate-300">·</span> {it}</li>
                ))}
            </ul>
        </div>
    );
}

// Pipeline simplifié — 6 étapes. "Reçu" et "Pré-rec assigné" fusionnés en "À appeler"
// car aucune différence opérationnelle pour le recruteur.
const FUNNEL_STEPS = [
    { key: "to_call", label: "À appeler", who: "Pré-rec",
      next_action: "Composer le numéro et lancer la scorecard téléphonique",
      maps_status: ["new", "prerec_assigned"] },
    { key: "prerec_done", label: "Pré-rec fait", who: "Pré-rec",
      next_action: "Décision : programmer immersion ou refuser",
      maps_status: ["prerec_done"] },
    { key: "immersion_scheduled", label: "Immersion prévue", who: "Référent terrain",
      next_action: "Évaluer le candidat sur le terrain (scorecard immersion)",
      maps_status: ["immersion_scheduled"] },
    { key: "immersion_done", label: "Immersion faite", who: "Admin / Référent",
      next_action: "Valider le recrutement ou refuser",
      maps_status: ["immersion_done", "awaiting_validation"] },
    { key: "validated", label: "Validé", who: "Admin",
      next_action: "Déclencher l'onboarding",
      maps_status: ["validated"] },
    { key: "onboarding", label: "Onboarding", who: "Admin",
      next_action: "Suivi du démarrage de poste",
      maps_status: ["onboarding", "hired"] },
];

function FunnelStrip({ status, candidate, onUnrejected }) {
    // Map the real status to its visible pipeline step
    const currentIdx = FUNNEL_STEPS.findIndex((s) => s.maps_status.includes(status));
    const currentStep = currentIdx >= 0 ? FUNNEL_STEPS[currentIdx] : null;
    if (status === "rejected") {
        const hasSubmittedForm = Boolean(candidate?.onboarding_form_id);
        const undoReject = async () => {
            try {
                await api.post(`/candidates/${candidate.id}/revert-status`);
                toast.success("Refus annulé — le candidat retrouve son parcours ✅");
                onUnrejected?.();
            } catch (e) {
                toast.error(e.response?.data?.detail || "Impossible d'annuler le refus");
            }
        };
        return (
            <div
                className="card-soft p-4 md:p-5 bg-red-50/40 border-red-200/60"
                data-testid="funnel-strip"
            >
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-red-100 text-red-700">
                            <ThumbsDown className="h-4 w-4" />
                        </span>
                        <div>
                            <div className="text-xs font-extrabold uppercase tracking-widest text-shariti-red">
                                Candidat refusé
                            </div>
                            {hasSubmittedForm && (
                                <div className="mt-0.5 text-[11px] font-semibold text-slate-600">
                                    Fiche d'embauche soumise — annulez le refus pour reprendre là où le process s'était arrêté.
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={undoReject}
                        data-testid="funnel-strip-undo-reject"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider px-4 py-2 transition shadow-sm"
                        title="Efface le refus — le candidat garde sa progression (fiche, docs, etc.)"
                    >
                        <Undo2 className="h-3.5 w-3.5" />
                        Annuler le refus
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="card-soft p-4 md:p-5" data-testid="funnel-strip">
            <div className="flex items-center gap-1 md:gap-2 overflow-x-auto">
                {FUNNEL_STEPS.map((step, i) => {
                    const done = i < currentIdx;
                    const active = i === currentIdx;
                    return (
                        <div key={step.key} className="flex items-center gap-1 md:gap-2 shrink-0">
                            <div
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition ${
                                    active
                                        ? "bg-shariti-orange text-white shadow-btn"
                                        : done
                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                        : "bg-shariti-cream text-slate-400"
                                }`}
                                title={`${step.next_action} (${step.who})`}
                            >
                                <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                        active
                                            ? "bg-white animate-pulse"
                                            : done
                                            ? "bg-emerald-500"
                                            : "bg-slate-300"
                                    }`}
                                />
                                {step.label}
                            </div>
                            {i < FUNNEL_STEPS.length - 1 && (
                                <div
                                    className={`h-px w-4 md:w-8 ${
                                        done ? "bg-emerald-300" : "bg-shariti-gray"
                                    }`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            {currentStep && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl bg-shariti-orange-soft/50 border border-shariti-orange/20 px-3 py-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange">
                        Action attendue
                    </span>
                    <span className="text-xs font-extrabold text-shariti-black">
                        {currentStep.next_action}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-auto">
                        Responsable : {currentStep.who}
                    </span>
                </div>
            )}
        </div>
    );
}
